import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, KeyRound, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const isInvite = searchParams.get('invite') === '1' || window.location.pathname === '/set-password';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing or invalid reset link. Please request a new one.');
      return;
    }
    if (!PASSWORD_RE.test(password)) {
      setError('Password must be at least 8 characters and include a letter and a number.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Reset failed. Please request a new link.');
      setDone(true);
      // Set-password flow logs the user in immediately — hop to the home
      // hub. Regular resets still land on /login so they authenticate fresh.
      setTimeout(() => navigate(data.logged_in ? '/' : '/login'), 1800);
    } catch (err) {
      setError(typeof err.message === 'string' ? err.message : 'Reset failed. Please request a new link.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#7A0B7A]/10 rounded-lg">
              <KeyRound className="w-5 h-5 text-[#7A0B7A]" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{isInvite ? 'Welcome — set your password' : 'Set a new password'}</h1>
          </div>
          {isInvite && (
            <p className="text-sm text-gray-600 mt-1 mb-2">Pick a password to finish setting up your Ox Resin Installer Portal account.</p>
          )}

          {!token && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 mt-4">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                The reset link is missing a token. <Link to="/forgot-password" className="font-semibold underline">Request a new one</Link>.
              </div>
            </div>
          )}

          {done ? (
            <div className="space-y-4 mt-4" data-testid="reset-success">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  Password updated. Redirecting you to the login page…
                </div>
              </div>
            </div>
          ) : token && (
            <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="reset-form">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={8}
                    data-testid="reset-password"
                    className="pl-10 pr-10 h-12 border-2 rounded-xl"
                  />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">At least 8 characters with a letter and a number.</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={show ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={8}
                    data-testid="reset-confirm"
                    className="pl-10 h-12 border-2 rounded-xl"
                  />
                </div>
              </div>
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="reset-error">{error}</div>
              )}
              <Button type="submit" disabled={submitting} data-testid="reset-submit" className="w-full h-12 bg-[#7A0B7A] hover:bg-[#5e0860] text-white font-bold">
                {submitting ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
